import type { AnalyticsConfig } from '@/api/generated/model';
import { chartTypeMap } from '@/api/requests/types/charts.type';
import { Granularity, RollingWindowConfigUnit } from '@/api/generated/model';

const ROLLING_WINDOW_CHART_TYPES = new Set<string>([chartTypeMap.line, chartTypeMap.stackedBar]);

export function isRollingWindowAllowed(
  config: Pick<AnalyticsConfig, 'chart_type' | 'visualization_type' | 'include_forecast' | 'granularity'>,
): boolean {
  const chartType = config.chart_type;
  const visualizationType = config.visualization_type ?? 'linear';

  return (
    ROLLING_WINDOW_CHART_TYPES.has(chartType) &&
    visualizationType === 'linear' &&
    !config.include_forecast &&
    config.granularity !== Granularity.hour
  );
}

export function isRollingWindowSizeTooLarge(size: number, unit: RollingWindowConfigUnit): boolean {
  if (unit === RollingWindowConfigUnit.day && size > 365) {
    return true;
  }
  if (unit === RollingWindowConfigUnit.week && size > 52) {
    return true;
  }
  if (unit === RollingWindowConfigUnit.month && size > 12) {
    return true;
  }
  return false;
}

export function sanitizeAnalyticsRollingWindow<T extends AnalyticsConfig>(config: T): T {
  if (!isRollingWindowAllowed(config)) {
    const { rolling_window: _rollingWindow, ...rest } = config;
    return rest as T;
  }

  const rollingWindow = config.rolling_window;
  if (
    !rollingWindow ||
    rollingWindow.size < 1 ||
    isRollingWindowSizeTooLarge(rollingWindow.size, rollingWindow.unit ?? RollingWindowConfigUnit.day)
  ) {
    const { rolling_window: _rollingWindow, ...rest } = config;
    return rest as T;
  }

  return config;
}

export function clearRollingWindowUnlessAllowed(config: AnalyticsConfig): AnalyticsConfig {
  if (isRollingWindowAllowed(config)) {
    return config;
  }

  const { rolling_window: _rollingWindow, ...rest } = config;
  return rest;
}
